import { FC, FormEvent, useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import CaresPageHeader from '../shell/cares-page-header';
import {
  createCalendarEvent,
  deleteCalendarEvent,
  listCalendarEvents,
  updateCalendarEvent,
  type CalendarEventPayload,
  type CalendarEventRecord,
} from './api';

type CalendarDraft = {
  id: string | null;
  title: string;
  details: string;
  start: string;
  end: string;
  allDay: boolean;
  timezone: string;
  source: string;
  type: string;
  status: string;
  location: string;
  color: string;
};

type CalendarLayerKey = 'shifts' | 'approvedRequests' | 'tentativeRequests' | 'admin' | 'privateEvents' | 'other';

type CalendarLayerState = Record<CalendarLayerKey, boolean>;
type CalendarColorState = Record<CalendarLayerKey, string>;
type LoadEventsOptions = {
  showLoading?: boolean;
  preserveEventsOnError?: boolean;
};

type CalendarRequestWorkflowMetadata = {
  kind?: string;
  request_type?: string;
  visibility?: 'private' | 'scheduling_admins';
  status?: 'private' | 'pending' | 'approved' | 'denied' | 'cancelled';
};

const requestWorkflowKinds = ['schedule_request', 'my_schedule_request'];

const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

const defaultCalendarLayers: CalendarLayerState = {
  shifts: true,
  admin: true,
  approvedRequests: true,
  tentativeRequests: true,
  privateEvents: true,
  other: true,
};

const calendarLayerLabels: Record<CalendarLayerKey, string> = {
  shifts: 'Clinical Shifts',
  admin: 'Admin',
  approvedRequests: 'Approved requests',
  tentativeRequests: 'Tentative requests',
  privateEvents: 'Private events',
  other: 'Other items',
};

const defaultCalendarColors: CalendarColorState = {
  shifts: '#0d6efd',
  admin: '#6f42c1',
  approvedRequests: '#198754',
  tentativeRequests: '#ffc107',
  privateEvents: '#20c997',
  other: '#6c757d',
};

const standardEventColors = [
  { label: 'Blue', value: '#0d6efd' },
  { label: 'Red', value: '#dc3545' },
  { label: 'Green', value: '#198754' },
  { label: 'Yellow', value: '#ffc107' },
  { label: 'Purple', value: '#6f42c1' },
  { label: 'Teal', value: '#20c997' },
  { label: 'Orange', value: '#fd7e14' },
  { label: 'Gray', value: '#6c757d' },
];

const calendarColorStorageKey = 'cares-calendar-default-colors-v2';
const calendarViewStorageKey = 'cares-calendar-view-v2';
const defaultCalendarView = 'dayGridMonth';
const calendarViewOptions = ['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'listWeek'];
const administrativeRequestTypes = ['admin', 'academic'];
const privateCalendarEventTypes = ['meeting', 'reminder', 'task', 'other'];

const isHexColor = (value?: string | null) => /^#[0-9a-f]{6}$/i.test(value ?? '');

const requestWorkflowFromEvent = (event: CalendarEventRecord): CalendarRequestWorkflowMetadata | null => {
  const workflow = event.metadata?.request_workflow;

  if (!workflow || typeof workflow !== 'object') return null;
  if (!requestWorkflowKinds.includes((workflow as CalendarRequestWorkflowMetadata).kind ?? '')) return null;

  return workflow as CalendarRequestWorkflowMetadata;
};

const isRequestWorkflowEvent = (event: CalendarEventRecord) => requestWorkflowFromEvent(event) !== null;

const isPrivateCalendarEvent = (event: CalendarEventRecord) => (
  event.source === 'manual'
    && privateCalendarEventTypes.includes(event.type)
    && !isRequestWorkflowEvent(event)
);

const isHiddenCancelledPrivateRequestEvent = (event: CalendarEventRecord) => {
  const workflow = requestWorkflowFromEvent(event);

  return workflow?.visibility === 'private' && workflow.status === 'cancelled';
};

const requestWorkflowBadgeLabel = (event: CalendarEventRecord) => {
  const workflow = requestWorkflowFromEvent(event);

  if (!workflow) return null;
  if (workflow.visibility === 'private') return 'Private request';
  if (workflow.status === 'pending') return 'Submitted request';
  if (workflow.status === 'approved') return 'Approved request';
  if (workflow.status === 'denied') return 'Denied request';
  if (workflow.status === 'cancelled') return 'Cancelled request';

  return 'Submitted request';
};

const sanitizeCalendarColors = (value: unknown): CalendarColorState => {
  if (!value || typeof value !== 'object') return defaultCalendarColors;

  return (Object.keys(defaultCalendarColors) as CalendarLayerKey[]).reduce<CalendarColorState>(
    (colors, layer) => ({
      ...colors,
      [layer]: isHexColor((value as Partial<CalendarColorState>)[layer])
        ? (value as Partial<CalendarColorState>)[layer] as string
        : defaultCalendarColors[layer],
    }),
    { ...defaultCalendarColors }
  );
};

const readCalendarColors = (): CalendarColorState => {
  if (typeof window === 'undefined') return defaultCalendarColors;

  try {
    return sanitizeCalendarColors(JSON.parse(window.localStorage.getItem(calendarColorStorageKey) ?? '{}'));
  } catch {
    return defaultCalendarColors;
  }
};

const readCalendarViewPreference = () => {
  if (typeof window === 'undefined') return defaultCalendarView;

  const savedView = window.localStorage.getItem(calendarViewStorageKey);

  return calendarViewOptions.includes(savedView ?? '') ? savedView as string : defaultCalendarView;
};

const resolveCalendarEventLayer = (event: CalendarEventRecord): CalendarLayerKey => {
  const workflow = requestWorkflowFromEvent(event);

  if (workflow && ['private', 'pending'].includes(workflow.status ?? '')) return 'tentativeRequests';
  if (workflow?.status === 'approved' && administrativeRequestTypes.includes(workflow.request_type ?? '')) return 'admin';
  if (workflow?.status === 'approved') return 'approvedRequests';
  if (event.type === 'shift') return 'shifts';
  if (isPrivateCalendarEvent(event)) return 'privateEvents';
  if (event.source !== 'manual' || ['time_off', 'availability'].includes(event.type)) return 'admin';

  return 'other';
};

const resolveDraftDefaultEventColor = (colors: CalendarColorState) => colors.privateEvents;

const resolveDefaultEventColor = (event: CalendarEventRecord, colors: CalendarColorState) => colors[resolveCalendarEventLayer(event)];

const emptyDraft = (color = defaultCalendarColors.privateEvents): CalendarDraft => ({
  id: null,
  title: '',
  details: '',
  start: '',
  end: '',
  allDay: true,
  timezone: defaultTimezone,
  source: 'manual',
  type: 'meeting',
  status: 'confirmed',
  location: '',
  color,
});

const pad = (value: number) => String(value).padStart(2, '0');

const toLocalDateInputValue = (value: string) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const toLocalDateTimeInputValue = (value: string) => {
  const date = new Date(value);
  return `${toLocalDateInputValue(value)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const localInputToIso = (value: string, allDay: boolean, boundary: 'start' | 'end') => {
  if (allDay) {
    return new Date(`${value}T${boundary === 'start' ? '00:00' : '23:59'}:00`).toISOString();
  }

  return new Date(value).toISOString();
};

const normalizeEventColor = (color?: string | null, fallback = defaultCalendarColors.privateEvents) => {
  const normalized = color?.trim();

  return isHexColor(normalized) ? normalized as string : fallback;
};

const resolveCalendarDisplayColor = (event: CalendarEventRecord, colors: CalendarColorState) => (
  isPrivateCalendarEvent(event)
    ? normalizeEventColor(event.color, colors.privateEvents)
    : resolveDefaultEventColor(event, colors)
);

const getReadableTextColor = (color: string) => {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return '#ffffff';

  const red = parseInt(color.slice(1, 3), 16);
  const green = parseInt(color.slice(3, 5), 16);
  const blue = parseInt(color.slice(5, 7), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return luminance > 0.6 ? '#212529' : '#ffffff';
};

const applyCalendarEventColor = (mount: {
  el: HTMLElement;
  event: {
    backgroundColor?: string;
    textColor?: string;
    extendedProps: { color?: string | null };
  };
}) => {
  const eventColor = normalizeEventColor(mount.event.extendedProps.color ?? mount.event.backgroundColor);
  const textColor = mount.event.textColor || getReadableTextColor(eventColor);

  mount.el.style.setProperty('--cares-calendar-event-bg', eventColor);
  mount.el.style.setProperty('--cares-calendar-event-text', textColor);
};

const applyCalendarEventElementColor = (element: HTMLElement, eventColor: string) => {
  element.style.setProperty('--cares-calendar-event-bg', eventColor);
  element.style.setProperty('--cares-calendar-event-text', getReadableTextColor(eventColor));
};

const draftFromEvent = (event: CalendarEventRecord): CalendarDraft => ({
  id: event.id,
  title: event.title,
  details: event.details ?? '',
  start: event.allDay ? toLocalDateInputValue(event.start) : toLocalDateTimeInputValue(event.start),
  end: event.allDay ? toLocalDateInputValue(event.end) : toLocalDateTimeInputValue(event.end),
  allDay: event.allDay,
  timezone: event.timezone ?? defaultTimezone,
  source: event.source,
  type: event.type,
  status: event.status,
  location: event.location ?? '',
  color: event.color ?? '#0d6efd',
});

const buildPayload = (draft: CalendarDraft): CalendarEventPayload => ({
  title: draft.title.trim(),
  details: draft.details.trim() || null,
  start: localInputToIso(draft.start, draft.allDay, 'start'),
  end: localInputToIso(draft.end, draft.allDay, 'end'),
  allDay: draft.allDay,
  timezone: draft.timezone || defaultTimezone,
  source: draft.source,
  type: draft.type,
  status: draft.status,
  location: draft.location.trim() || null,
  color: draft.color || null,
  metadata: {},
});

const eventMatchesCalendarLayer = (event: CalendarEventRecord, layer: CalendarLayerKey) => resolveCalendarEventLayer(event) === layer;

const isEventVisibleForLayers = (event: CalendarEventRecord, layers: CalendarLayerState) => layers[resolveCalendarEventLayer(event)];

const isCancelledCalendarEvent = (event: CalendarEventRecord) => {
  const workflow = requestWorkflowFromEvent(event);

  return event.status === 'cancelled' || workflow?.status === 'cancelled';
};

const isUpcomingListEvent = (event: CalendarEventRecord) => {
  if (isCancelledCalendarEvent(event)) return false;

  const workflow = requestWorkflowFromEvent(event);
  const layer = resolveCalendarEventLayer(event);
  const isShift = layer === 'shifts' || (layer === 'admin' && !workflow);
  const isIncludedRequest = workflow?.status === 'approved' || workflow?.status === 'private';

  return isShift || isIncludedRequest || isPrivateCalendarEvent(event);
};

const CalendarPage: FC = () => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const mountedEventElementsRef = useRef(new Map<string, Set<HTMLElement>>());
  const [events, setEvents] = useState<CalendarEventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState<CalendarDraft>(emptyDraft);
  const [calendarLayers, setCalendarLayers] = useState<CalendarLayerState>(defaultCalendarLayers);
  const [calendarColors, setCalendarColors] = useState<CalendarColorState>(() => readCalendarColors());
  const [activeLayerPalette, setActiveLayerPalette] = useState<CalendarLayerKey | null>(null);
  const [showEventPalette, setShowEventPalette] = useState(false);
  const [activeCalendarView, setActiveCalendarView] = useState(() => readCalendarViewPreference());
  const [hasLoadedEvents, setHasLoadedEvents] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const scheduleEvents = events.filter((event) => !isHiddenCancelledPrivateRequestEvent(event));
  const visibleEvents = scheduleEvents.filter((event) => isEventVisibleForLayers(event, calendarLayers));
  const hiddenEventCount = scheduleEvents.length - visibleEvents.length;
  const calendarLayerKeys = Object.keys(calendarLayerLabels) as CalendarLayerKey[];
  const allCalendarLayersEnabled = calendarLayerKeys.every((layer) => calendarLayers[layer]);
  const layerCounts = scheduleEvents.reduce<Record<CalendarLayerKey, number>>(
    (counts, event) => {
      (Object.keys(counts) as CalendarLayerKey[]).forEach((layer) => {
        if (eventMatchesCalendarLayer(event, layer)) counts[layer] += 1;
      });
      return counts;
    },
    {
      shifts: 0,
      admin: 0,
      approvedRequests: 0,
      tentativeRequests: 0,
      privateEvents: 0,
      other: 0,
    }
  );

  const upcomingEvents = [...visibleEvents]
    .filter((event) => isUpcomingListEvent(event) && new Date(event.end).getTime() >= Date.now())
    .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime())
    .slice(0, 5);
  const now = Date.now();
  const sevenDaysFromNow = now + (7 * 24 * 60 * 60 * 1000);
  const summaryDefinitions: Array<{ key: CalendarLayerKey; label: string; nextSevenDays: boolean }> = [
    { key: 'shifts', label: 'Clinical Shifts', nextSevenDays: true },
    { key: 'admin', label: 'Admin', nextSevenDays: true },
    { key: 'approvedRequests', label: 'Approved Requests', nextSevenDays: false },
    { key: 'tentativeRequests', label: 'Tentative Requests', nextSevenDays: false },
    { key: 'privateEvents', label: 'Private Events', nextSevenDays: false },
    { key: 'other', label: 'Other Events', nextSevenDays: false },
  ];
  const summaryEvents = summaryDefinitions.map((summary) => ({
    ...summary,
    events: scheduleEvents
      .filter((event) => {
        const start = new Date(event.start).getTime();
        return !isCancelledCalendarEvent(event)
          && resolveCalendarEventLayer(event) === summary.key
          && start >= now
          && (!summary.nextSevenDays || start <= sevenDaysFromNow);
      })
      .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime()),
  }));
  const initialLoadFailed = !loading && !hasLoadedEvents && loadError !== null;

  const toggleCalendarLayer = (layer: CalendarLayerKey) => {
    setCalendarLayers((current) => ({ ...current, [layer]: !current[layer] }));
  };

  const toggleAllCalendarLayers = () => {
    const nextEnabled = !allCalendarLayersEnabled;
    setCalendarLayers(
      calendarLayerKeys.reduce<CalendarLayerState>(
        (layers, layer) => ({ ...layers, [layer]: nextEnabled }),
        { ...defaultCalendarLayers }
      )
    );
  };

  const changeCalendarView = (view: string) => {
    calendarRef.current?.getApi().changeView(view);
    setActiveCalendarView(view);
    window.localStorage.setItem(calendarViewStorageKey, view);
  };

  const loadEvents = async ({ showLoading = true, preserveEventsOnError = false }: LoadEventsOptions = {}) => {
    if (showLoading) {
      setLoading(true);
    }
    setLoadError(null);
    setRefreshError(null);

    try {
      const nextEvents = await listCalendarEvents();
      setEvents(nextEvents);
      setHasLoadedEvents(true);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to load calendar events.';

      if (preserveEventsOnError && hasLoadedEvents) {
        setRefreshError(`Unable to refresh calendar data. Showing your last loaded schedule. ${message}`);
      } else {
        setLoadError(message);
        setEvents([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadEvents();
  }, []);

  useEffect(() => {
    const refreshEvents = () => void loadEvents({ showLoading: false, preserveEventsOnError: true });
    const refreshVisibleEvents = () => {
      if (document.visibilityState === 'visible') refreshEvents();
    };

    window.addEventListener('focus', refreshEvents);
    document.addEventListener('visibilitychange', refreshVisibleEvents);

    return () => {
      window.removeEventListener('focus', refreshEvents);
      document.removeEventListener('visibilitychange', refreshVisibleEvents);
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem(calendarColorStorageKey, JSON.stringify(calendarColors));
  }, [calendarColors]);

  const resetModal = () => {
    setDraft(emptyDraft(resolveDraftDefaultEventColor(calendarColors)));
    setShowEventPalette(false);
    setShowModal(false);
  };

  const retryLoadEvents = () => {
    void loadEvents({ showLoading: true, preserveEventsOnError: hasLoadedEvents });
  };

  const openCreateModal = () => {
    setDraft(emptyDraft(resolveDraftDefaultEventColor(calendarColors)));
    setOperationError(null);
    setSuccess(null);
    setShowModal(true);
  };

  const openEditModal = (event: CalendarEventRecord) => {
    if (!isPrivateCalendarEvent(event)) return;

    setDraft({
      ...draftFromEvent(event),
      color: normalizeEventColor(event.color, resolveDefaultEventColor(event, calendarColors)),
    });
    setOperationError(null);
    setSuccess(null);
    setShowModal(true);
  };

  const handleSlotSelect = (selection: {
    start: Date;
    end: Date;
    allDay: boolean;
  }) => {
    setDraft({
      ...emptyDraft(resolveDraftDefaultEventColor(calendarColors)),
      start: toLocalDateInputValue(selection.start.toISOString()),
      end: toLocalDateInputValue(selection.start.toISOString()),
      allDay: true,
    });
    setOperationError(null);
    setSuccess(null);
    setShowModal(true);
  };

  const handleEventClick = (click: { event: { id: string } }) => {
    const match = events.find((event) => event.id === click.event.id);
    if (match) {
      openEditModal(match);
    }
  };

  const updateCalendarDefaultColor = (layer: CalendarLayerKey, color: string) => {
    if (!isHexColor(color)) return;

    setCalendarColors((current) => ({ ...current, [layer]: color }));
  };

  const updateDraftType = (type: string) => {
    setDraft((current) => {
      const currentDefaultColor = resolveDraftDefaultEventColor(calendarColors);
      const nextDraft = { ...current, type };

      return {
        ...nextDraft,
        color: current.color === currentDefaultColor ? resolveDraftDefaultEventColor(calendarColors) : current.color,
      };
    });
  };

  const updateDraftTentative = (tentative: boolean) => {
    setDraft((current) => {
      const currentDefaultColor = resolveDraftDefaultEventColor(calendarColors);
      const nextDraft = { ...current, status: tentative ? 'tentative' : 'confirmed' };

      return {
        ...nextDraft,
        color: current.color === currentDefaultColor ? resolveDraftDefaultEventColor(calendarColors) : current.color,
      };
    });
  };

  const syncMountedCalendarEventColor = (event: CalendarEventRecord) => {
    const mountedEvent = calendarRef.current?.getApi().getEventById(event.id);
    if (!mountedEvent) return;

    const eventColor = resolveCalendarDisplayColor(event, calendarColors);
    mountedEvent.setProp('backgroundColor', eventColor);
    mountedEvent.setProp('borderColor', eventColor);
    mountedEvent.setProp('textColor', getReadableTextColor(eventColor));
    mountedEvent.setExtendedProp('color', eventColor);
    mountedEventElementsRef.current.get(event.id)?.forEach((element) => {
      applyCalendarEventElementColor(element, eventColor);
    });
  };

  const updateDraftStart = (value: string) => {
    setDraft((current) => ({
      ...current,
      start: value,
      end: value,
    }));
  };

  const persistMove = async (eventId: string, payload: Partial<CalendarEventPayload>) => {
    setOperationError(null);

    try {
      await updateCalendarEvent(eventId, payload);
      await loadEvents({ showLoading: false, preserveEventsOnError: true });
    } catch (requestError) {
      setOperationError(requestError instanceof Error ? requestError.message : 'Unable to update the event.');
      throw requestError;
    }
  };

  const handleEventDrop = async (change: {
    event: {
      id: string;
      start: Date | null;
      end: Date | null;
      allDay: boolean;
    };
    revert: () => void;
  }) => {
    const calendarEvent = events.find((event) => event.id === change.event.id);

    if (calendarEvent && !isPrivateCalendarEvent(calendarEvent)) {
      change.revert();
      return;
    }

    try {
      await persistMove(change.event.id, {
        start: change.event.start?.toISOString() ?? '',
        end: (change.event.end ?? change.event.start)?.toISOString() ?? '',
        allDay: change.event.allDay,
      });
    } catch {
      change.revert();
    }
  };

  const handleEventResize = async (change: {
    event: {
      id: string;
      start: Date | null;
      end: Date | null;
      allDay: boolean;
    };
    revert: () => void;
  }) => {
    const calendarEvent = events.find((event) => event.id === change.event.id);

    if (calendarEvent && !isPrivateCalendarEvent(calendarEvent)) {
      change.revert();
      return;
    }

    try {
      await persistMove(change.event.id, {
        start: change.event.start?.toISOString() ?? '',
        end: (change.event.end ?? change.event.start)?.toISOString() ?? '',
        allDay: change.event.allDay,
      });
    } catch {
      change.revert();
    }
  };

  const submitEvent = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    setSaving(true);
    setOperationError(null);
    setSuccess(null);

    try {
      if (!draft.title.trim()) {
        throw new Error('A title is required.');
      }

      const payload = buildPayload(draft);

      if (draft.id) {
        const updatedEvent = await updateCalendarEvent(draft.id, payload);
        syncMountedCalendarEventColor(updatedEvent);
        setSuccess('Calendar event updated.');
      } else {
        await createCalendarEvent(payload);
        setSuccess('Calendar event created.');
      }

      await loadEvents({ showLoading: false, preserveEventsOnError: true });
      resetModal();
    } catch (requestError) {
      setOperationError(requestError instanceof Error ? requestError.message : 'Unable to save the event.');
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async () => {
    if (!draft.id) return;

    setSaving(true);
    setOperationError(null);
    setSuccess(null);

    try {
      await deleteCalendarEvent(draft.id);
      await loadEvents({ showLoading: false, preserveEventsOnError: true });
      resetModal();
      setSuccess('Calendar event deleted.');
    } catch (requestError) {
      setOperationError(requestError instanceof Error ? requestError.message : 'Unable to delete the event.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="My Schedule" actions={<></>} />

      <style>{`
        .cares-calendar .cares-calendar-event {
          background-color: var(--cares-calendar-event-bg, #0d6efd) !important;
          border-color: var(--cares-calendar-event-bg, #0d6efd) !important;
          color: var(--cares-calendar-event-text, #ffffff) !important;
        }

        .cares-calendar .cares-calendar-event .fc-event-main,
        .cares-calendar .cares-calendar-event .fc-event-title,
        .cares-calendar .cares-calendar-event .fc-event-time {
          color: var(--cares-calendar-event-text, #ffffff) !important;
        }

        .cares-calendar .fc .fc-scrollgrid-section-header,
        .cares-calendar .fc .fc-scrollgrid-section-header > *,
        .cares-calendar .fc .fc-col-header,
        .cares-calendar .fc .fc-col-header-cell,
        .cares-calendar .fc .fc-timegrid-axis,
        .cares-calendar .fc .fc-timegrid-axis-frame,
        .cares-calendar .fc .fc-daygrid-week-number {
          background-color: transparent !important;
          background: transparent !important;
        }
      `}</style>

      <Row className="g-4">
        <Col xl={9}>
          <Card className="custom-card cares-page-hero cares-calendar">
            <Card.Header className="justify-content-between align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Card.Title className="mb-0">Calendar</Card.Title>
                <div className="btn-group" role="group" aria-label="Calendar view">
                  {[
                    { label: 'Month', view: 'dayGridMonth' },
                    { label: 'Week', view: 'timeGridWeek' },
                    { label: 'Day', view: 'timeGridDay' },
                    { label: 'List', view: 'listWeek' },
                  ].map((item) => (
                    <Button
                      key={item.view}
                      type="button"
                      variant={activeCalendarView === item.view ? 'primary' : 'light'}
                      size="sm"
                      className="border"
                      onClick={() => changeCalendarView(item.view)}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>
              <Button onClick={openCreateModal}>New Private Event</Button>
            </Card.Header>
            <Card.Body>
              {operationError && !showModal ? <Alert variant="danger">{operationError}</Alert> : null}
              {refreshError ? (
                <Alert variant="warning" className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                  <span>{refreshError}</span>
                  <Button type="button" variant="outline-warning" size="sm" onClick={retryLoadEvents}>
                    Retry refresh
                  </Button>
                </Alert>
              ) : null}
              {success ? <Alert variant="success">{success}</Alert> : null}
              {loading ? (
                <div className="text-center p-5"><Spinner animation="border" /></div>
              ) : initialLoadFailed ? (
                <Alert variant="danger" className="mb-0">
                  <div className="fw-semibold mb-2">Calendar unavailable</div>
                  <p className="mb-3">
                    {loadError}
                  </p>
                  <Button type="button" variant="outline-danger" onClick={retryLoadEvents}>
                    Retry loading calendar
                  </Button>
                </Alert>
              ) : (
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView={activeCalendarView}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: '',
                  }}
                  events={visibleEvents.map((event) => {
                    const eventColor = resolveCalendarDisplayColor(event, calendarColors);

                    return {
                      id: event.id,
                      title: event.title,
                      start: event.start,
                      end: event.end,
                      allDay: event.allDay,
                      color: eventColor,
                      backgroundColor: eventColor,
                      borderColor: eventColor,
                      textColor: getReadableTextColor(eventColor),
                      classNames: ['cares-calendar-event'],
                      editable: isPrivateCalendarEvent(event),
                      startEditable: isPrivateCalendarEvent(event),
                      durationEditable: isPrivateCalendarEvent(event),
                      extendedProps: { color: eventColor },
                    };
                  })}
                  editable={true}
                  selectable={true}
                  selectMirror={true}
                  dayMaxEvents={true}
                  select={handleSlotSelect}
                  eventClick={handleEventClick}
                  eventDidMount={(mount) => {
                    applyCalendarEventColor(mount);
                    const mountedElements = mountedEventElementsRef.current.get(mount.event.id) ?? new Set<HTMLElement>();
                    mountedElements.add(mount.el);
                    mountedEventElementsRef.current.set(mount.event.id, mountedElements);
                  }}
                  eventWillUnmount={(mount) => {
                    const mountedElements = mountedEventElementsRef.current.get(mount.event.id);
                    mountedElements?.delete(mount.el);
                    if (mountedElements?.size === 0) mountedEventElementsRef.current.delete(mount.event.id);
                  }}
                  eventDrop={(info) => void handleEventDrop(info)}
                  eventResize={(info) => void handleEventResize(info)}
                  height="auto"
                />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3}>
          <Card className="custom-card">
            <Card.Body>
              {initialLoadFailed ? (
                <>
                  <p className="text-primary text-uppercase fs-12 fw-semibold mb-2">CARES calendar</p>
                  <h5 className="mb-2">Calendar layers unavailable</h5>
                  <p className="text-muted mb-3">
                    Layer counts stay hidden until the calendar loads successfully. Retry the calendar request to restore the schedule view.
                  </p>
                  <Button type="button" variant="outline-primary" size="sm" className="w-100" onClick={retryLoadEvents}>
                    Retry loading calendar
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-primary text-uppercase fs-12 fw-semibold mb-2">CARES calendar</p>
                  <h5 className="mb-2">Calendar layers</h5>
                  <p className="text-muted mb-3">
                    Toggle schedule layers without leaving the calendar. Request submission and management stay in My Requests.
                  </p>
                  <Button
                    type="button"
                    variant={allCalendarLayersEnabled ? 'dark' : 'primary'}
                    size="sm"
                    className="w-100 mb-3"
                    onClick={toggleAllCalendarLayers}
                  >
                    {allCalendarLayersEnabled ? 'Turn all layers off' : 'Turn all layers on'}
                  </Button>
                  <div className="d-flex flex-column gap-2">
                    {calendarLayerKeys.map((layer) => (
                      <div
                        key={layer}
                        className="d-flex align-items-center justify-content-between gap-3 border rounded p-2"
                      >
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                          <Form.Check
                            type="switch"
                            id={`calendar-layer-${layer}`}
                            label={calendarLayerLabels[layer]}
                            checked={calendarLayers[layer]}
                            onChange={() => toggleCalendarLayer(layer)}
                          />
                        </div>
                        <div className="d-flex align-items-center justify-content-end gap-2 position-relative">
                          <Button
                            type="button"
                            variant="light"
                            className="border p-1"
                            title={`${calendarLayerLabels[layer]} default color`}
                            aria-label={`Open ${calendarLayerLabels[layer]} color palette`}
                            onClick={() => setActiveLayerPalette((current) => (current === layer ? null : layer))}
                          >
                            <span
                              aria-hidden="true"
                              className="d-block rounded"
                              style={{ width: '1.45rem', height: '1.25rem', backgroundColor: calendarColors[layer] }}
                            />
                          </Button>
                          {activeLayerPalette === layer ? (
                            <div
                              className="position-absolute end-0 top-100 mt-2 bg-white border rounded shadow-sm p-2"
                              style={{ width: '12rem', zIndex: 5 }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <Form.Control
                                  type="color"
                                  value={calendarColors[layer]}
                                  title={`${calendarLayerLabels[layer]} custom color`}
                                  onChange={(event) => updateCalendarDefaultColor(layer, event.target.value)}
                                  className="p-1"
                                  style={{ width: '2.5rem', height: '2rem' }}
                                />
                                <span className="text-muted fs-12">Custom</span>
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {standardEventColors.map((color) => (
                                  <Button
                                    key={color.value}
                                    type="button"
                                    variant="light"
                                    className="border rounded-circle p-0"
                                    title={color.label}
                                    aria-label={`Set ${calendarLayerLabels[layer]} color to ${color.label}`}
                                    aria-pressed={calendarColors[layer].toLowerCase() === color.value.toLowerCase()}
                                    onClick={() => updateCalendarDefaultColor(layer, color.value)}
                                    style={{
                                      width: '1.75rem',
                                      height: '1.75rem',
                                      backgroundColor: color.value,
                                      borderColor: calendarColors[layer].toLowerCase() === color.value.toLowerCase() ? '#000' : color.value,
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          ) : null}
                          <Badge
                            bg=""
                            className="border bg-transparent"
                            style={{
                              borderColor: calendarColors[layer],
                              color: calendarColors[layer],
                            }}
                          >
                            {layerCounts[layer]}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Badge bg="" className="bg-primary-transparent text-primary">{visibleEvents.length} visible</Badge>
                    <Badge bg="" className="bg-secondary-transparent text-secondary">{hiddenEventCount} hidden</Badge>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
          <Card className="custom-card">
            <Card.Header>
              <Card.Title>Upcoming</Card.Title>
            </Card.Header>
            <Card.Body>
              {initialLoadFailed ? (
                <p className="text-muted mb-0">Upcoming items are unavailable until the calendar loads successfully.</p>
              ) : upcomingEvents.length ? (
                <div className="d-flex flex-column gap-3">
                  {upcomingEvents.map((event) => {
                    const eventColor = resolveCalendarDisplayColor(event, calendarColors);
                    const textColor = getReadableTextColor(eventColor);
                    const badgeStyle = {
                      backgroundColor: textColor === '#ffffff' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(255, 255, 255, 0.7)',
                      borderColor: textColor === '#ffffff' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.08)',
                      color: textColor,
                    };

                    return (
                      <button
                        key={event.id}
                        type="button"
                        className="btn text-start border w-100"
                        disabled={!isPrivateCalendarEvent(event)}
                        title={!isPrivateCalendarEvent(event) ? 'Only private events can be edited here.' : undefined}
                        onClick={() => openEditModal(event)}
                        style={{
                          backgroundColor: eventColor,
                          borderColor: eventColor,
                          color: textColor,
                          opacity: 1,
                        }}
                      >
                        <div className="fw-semibold">{event.title}</div>
                        <div className="fs-12" style={{ color: textColor }}>
                          {new Date(event.start).toLocaleString()} to {new Date(event.end).toLocaleString()}
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {requestWorkflowBadgeLabel(event) ? (
                            <Badge bg="" className="border" style={badgeStyle}>{requestWorkflowBadgeLabel(event)}</Badge>
                          ) : null}
                          <Badge bg="" className="border" style={badgeStyle}>{event.type}</Badge>
                          <Badge bg="" className="border" style={badgeStyle}>{event.status}</Badge>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted mb-0">No upcoming events yet. Select a slot or create one manually.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mt-0">
        {summaryEvents.map((summary) => (
          <Col key={summary.key} xxl={4} md={6}>
            <Card className="custom-card h-100">
              <Card.Header className="justify-content-between align-items-center">
                <Card.Title className="mb-0">{summary.label}</Card.Title>
                <Badge bg="light" text="dark" className="border">{summary.events.length}</Badge>
              </Card.Header>
              <Card.Body>
                {initialLoadFailed ? (
                  <p className="text-muted mb-0">This list is unavailable until the calendar loads successfully.</p>
                ) : summary.events.length ? (
                  <div className="list-group list-group-flush">
                    {summary.events.map((event) => (
                      <button
                        key={event.id}
                        type="button"
                        className="list-group-item list-group-item-action px-0"
                        disabled={!isPrivateCalendarEvent(event)}
                        onClick={() => openEditModal(event)}
                      >
                        <div className="d-flex align-items-start gap-2">
                          <span
                            className="rounded-circle flex-shrink-0 mt-1"
                            aria-hidden="true"
                            style={{ width: '0.65rem', height: '0.65rem', backgroundColor: resolveCalendarDisplayColor(event, calendarColors) }}
                          />
                          <span className="flex-grow-1 text-start">
                            <span className="d-block fw-semibold">{event.title}</span>
                            <span className="d-block text-muted fs-12">
                              {new Date(event.start).toLocaleString()}
                              {event.location ? ` · ${event.location}` : ''}
                            </span>
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">
                    No {summary.label.toLowerCase()} {summary.nextSevenDays ? 'in the next 7 days.' : 'scheduled.'}
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={showModal} onHide={resetModal} centered size="lg">
        <Form onSubmit={submitEvent}>
          <Modal.Header closeButton>
            <Modal.Title>{draft.id ? 'Edit Calendar Event' : 'New Calendar Event'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {operationError && showModal ? <Alert variant="danger">{operationError}</Alert> : null}
            <Row className="gy-3">
              <Col md={8}>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={draft.title}
                  onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                  required
                />
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Form.Check
                  type="checkbox"
                  id="calendar-event-tentative"
                  label="Tentative"
                  checked={draft.status === 'tentative'}
                  onChange={(event) => updateDraftTentative(event.target.checked)}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={draft.details}
                  onChange={(event) => setDraft((current) => ({ ...current, details: event.target.value }))}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Start</Form.Label>
                <Form.Control
                  type={draft.allDay ? 'date' : 'datetime-local'}
                  value={draft.start}
                  onChange={(event) => updateDraftStart(event.target.value)}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>End</Form.Label>
                <Form.Control
                  type={draft.allDay ? 'date' : 'datetime-local'}
                  value={draft.end}
                  onChange={(event) => setDraft((current) => ({ ...current, end: event.target.value }))}
                  required
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="calendar-all-day"
                  label="All day"
                  checked={draft.allDay}
                  onChange={(event) =>
                    setDraft((current) => {
                      const nextAllDay = event.target.checked;

                      return {
                        ...current,
                        allDay: nextAllDay,
                        start: current.start
                          ? (nextAllDay ? current.start.slice(0, 10) : `${current.start.slice(0, 10)}T09:00`)
                          : current.start,
                        end: current.end
                          ? (nextAllDay ? current.end.slice(0, 10) : `${current.end.slice(0, 10)}T17:00`)
                          : current.end,
                      };
                    })
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Label>Type</Form.Label>
                <Form.Select
                  value={draft.type}
                  onChange={(event) => updateDraftType(event.target.value)}
                >
                  <option value="meeting">Meeting</option>
                  <option value="reminder">Reminder</option>
                  <option value="task">Task</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Timezone</Form.Label>
                <Form.Control
                  value={draft.timezone}
                  onChange={(event) => setDraft((current) => ({ ...current, timezone: event.target.value }))}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Location</Form.Label>
                <Form.Control
                  value={draft.location}
                  onChange={(event) => setDraft((current) => ({ ...current, location: event.target.value }))}
                  placeholder="Clinic, telehealth, room..."
                />
              </Col>
              <Col md={8}>
                <Form.Label>Color</Form.Label>
                <div className="position-relative">
                  <Button
                    type="button"
                    variant="light"
                    className="border d-inline-flex align-items-center gap-2"
                    onClick={() => setShowEventPalette((current) => !current)}
                    aria-label="Open event color palette"
                  >
                    <span
                      aria-hidden="true"
                      className="rounded"
                      style={{ width: '1.5rem', height: '1.25rem', backgroundColor: draft.color }}
                    />
                    <span className="text-muted fs-12">Color</span>
                  </Button>
                  {showEventPalette ? (
                    <div
                      className="position-absolute start-0 top-100 mt-2 bg-white border rounded shadow-sm p-2"
                      style={{ width: '13rem', zIndex: 5 }}
                    >
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Form.Control
                          type="color"
                          value={draft.color}
                          title="Custom event color"
                          onChange={(event) => setDraft((current) => ({ ...current, color: event.target.value }))}
                          className="p-1"
                          style={{ width: '2.5rem', height: '2rem' }}
                        />
                        <span className="text-muted fs-12">Custom</span>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {standardEventColors.map((color) => (
                          <Button
                            key={color.value}
                            type="button"
                            size="sm"
                            variant="light"
                            className="border rounded-circle p-0"
                            title={color.label}
                            aria-label={`Set event color to ${color.label}`}
                            onClick={() => setDraft((current) => ({ ...current, color: color.value }))}
                            aria-pressed={draft.color.toLowerCase() === color.value.toLowerCase()}
                            style={{
                              width: '1.75rem',
                              height: '1.75rem',
                              backgroundColor: color.value,
                              borderColor: draft.color.toLowerCase() === color.value.toLowerCase() ? '#000' : color.value,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <div>
              {draft.id ? (
                <Button type="button" variant="outline-danger" onClick={() => void removeEvent()} disabled={saving}>
                  Delete
                </Button>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
              <span className="text-muted fs-12">Events are for private viewing only and not submitted to admin.</span>
              <div className="d-flex gap-2">
                <Button type="button" variant="light" onClick={resetModal} disabled={saving}>Cancel</Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : draft.id ? 'Save Changes' : 'Create Event'}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default CalendarPage;
